package com.exura.pages.planCreator.components

import androidx.compose.runtime.*
import com.exura.components.Icon
import com.exura.features.plandefinition.domain.model.collection.Collection
import com.exura.features.plandefinition.domain.model.series.BaseAnchoredSeries
import com.exura.features.plandefinition.planLocalization
import com.exura.pages.planCreator.model.SeriesTypeRegistry
import com.exura.ui.*
import com.exura.ui.modifiers.insetHoverableItemStyle
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.plus
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.times

/**
 * Left panel showing list of current series with "Add Series" button in edit mode
 */
@Composable
fun SeriesListPanel(
    modifier: Modifier = Modifier,
    viewMode: ViewMode,
    flattenedItems: List<FlattenedItem>,
    selectedIndexedSeries: IndexedSeries?,
    onSeriesSelected: (IndexedSeries?) -> Unit,
    seriesGroupingMode: SeriesGroupingMode,
    onSeriesGroupingModeChange: (SeriesGroupingMode) -> Unit,
    onAddSeriesClick: () -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxHeight()
            .backgroundColor(ExuraColors.WHITE)
//            .borderRight { ExuraColors.BORDER_LIGHT }
            .padding(16.px)
    ) {
        // Header with grouping controls
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 16.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                text = "Serie",
                modifier = Modifier.fontWeight(FontWeight.Bold)
            )

            // Grouping toggle button
            Box(
                modifier = Modifier
                    .padding(8.px)
                    .borderRadius(8.px)
                    .onClick {
                        val newMode = if (seriesGroupingMode == SeriesGroupingMode.GROUPED) {
                            SeriesGroupingMode.UNGROUPED
                        } else {
                            SeriesGroupingMode.GROUPED
                        }
                        onSeriesGroupingModeChange(newMode)
                    },
            ) {
                Icon(
                    // PLACEHOLDER: Add appropriate group/ungroup icon
                    // Could be "group" for grouped mode, "list" for ungrouped
                    iconSrc = Assets.IconsUntiled.PLACEHOLDER, // TODO: Replace with appropriate icon
                    size = 16.px
                )
            }
        }

        // Series list
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
                .overflow(Overflow.Hidden, Overflow.Auto),
            verticalArrangement = Arrangement.spacedBy(8.px)
        ) {
            flattenedItems.forEach { item ->
                when (item) {
                    is FlattenedItem.CollectionHeader -> {
                        CollectionHeaderItem(
                            collection = item.collection,
                            collectionIndex = item.collectionIndex
                        )
                    }
                    is FlattenedItem.SeriesItem -> {
                        SeriesListItem(
                            indexedSeries = item.indexedSeries,
                            isSelected = selectedIndexedSeries == item.indexedSeries,
                            onClick = { onSeriesSelected(item.indexedSeries) },
                            showIndentation = seriesGroupingMode == SeriesGroupingMode.GROUPED &&
                                    item.indexedSeries.isFromCollection
                        )
                    }
                }
            }
        }

        // Add series button (only in edit mode)
        if (viewMode == ViewMode.EDIT) {
            AddSeriesButton(
                onClick = onAddSeriesClick
            )
        }
    }
}

@Composable
private fun AddSeriesButton(
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .insetHoverableItemStyle(
                isSelected = false,
                onClick = onClick
            )
            .padding(12.px),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            iconSrc = Assets.IconsUntiled.ADD_SERIES,
            color = ExuraColors.BLUE,
            size = 24.px
        )

        SpanText(
            text = "Dodaj serię",
            modifier = Modifier
                .classNames(TextStyles.medium14)
                .color(ExuraColors.BLUE)
                .margin(left = 8.px)
        )
    }
}


@Composable
private fun CollectionHeaderItem(
    collection: Collection,
    collectionIndex: Int
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(topBottom = 8.px)
            .backgroundColor(ExuraColors.TRANSPARENT)
            .borderRadius(8.px)
            .padding(12.px)
    ) {
        Column {
            SpanText(
                text = collection.name,
                modifier = Modifier
                    .classNames(TextStyles.medium14)
                    .color(ExuraColors.ARSENIC2)
            )
//            SpanText(
//                text = collection.description,
//                modifier = Modifier
//                    .fontSize(TextStyles.Body.ExtraSmall.fontSize)
//                    .color(ExuraColors.TEXT_SECONDARY)
//                    .margin(top = .px_EXTRA_SMALL)
//            )
        }
    }
}
@Composable
private fun SeriesListItem(
    indexedSeries: IndexedSeries,
    isSelected: Boolean,
    showIndentation: Boolean = false,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    val series = indexedSeries.series
    val typeInfo = SeriesTypeRegistry.getTypeInfo(series)
    val duration = series.durationInDays()

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .insetHoverableItemStyle(
                isSelected = isSelected,
                onClick = onClick
            )
            .padding(12.px)
            .padding(left = if (showIndentation) 18.px else 12.px)
            .then(modifier),
        horizontalArrangement = Arrangement.spacedBy(12.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .minSize(8.px * 2 + 24.px )
                .maxSize(8.px * 2 + 24.px )
        ) {
            typeInfo?.icon?.invoke()
        }

        Column(
            modifier = Modifier.weight(1f).fillMaxHeight().overflow(Overflow.Hidden).textOverflow(TextOverflow.Ellipsis),
            verticalArrangement = Arrangement.spacedBy(4.px),
        ) {
            // Series title (with ellipsis if overflow)
            SpanText(
                text = series.title,
                modifier = Modifier
                    .fillMaxWidth()
                    .classNames(TextStyles.medium14)
                    .color(ExuraColors.ARSENIC)
                    .textOverflow(TextOverflow.Ellipsis)
                    .whiteSpace(WhiteSpace.NoWrap)
                    .overflow(Overflow.Hidden)
            )

            // Second row: Day info and duration with proper spacing
            Row(
                modifier = Modifier
                    .fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (series is BaseAnchoredSeries) {
                    SpanText(
                        text = "Dzień ${series.relativeDay}",
                        modifier = Modifier
                            .classNames(TextStyles.normal12)
                            .color(ExuraColors.ARSENIC)
                    )
                }

                SpanText(
                    text = if (duration != null) planLocalization.current.daysCount(duration) else "? dni",
                    modifier = Modifier
                        .classNames(TextStyles.normal12)
                        .color(ExuraColors.ARSENIC)
                )
            }
        }
    }
}
